import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { servicesData } from "./ServicesData";
import {
  Phone,
  Mail,
  Send,
  ChevronRight,
  CheckCircle2,
  Globe,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

const ServicesDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  const service = servicesData.find(
    (s) => s.id.toLowerCase().trim() === serviceId?.toLowerCase().trim(),
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: service?.title || "",
    message: "",
  });

  const [success, setSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      setFormData((prev) => ({ ...prev, topic: service.title }));
    }
  }, [serviceId, service]);

  if (!service) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen flex flex-col items-center justify-center text-white">
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <p className="text-gray-400 mb-8">
          The service you are looking for does not exist.
        </p>
        <Link
          to="/"
          className="bg-[#B68D40] text-black font-bold px-8 py-3 rounded-xl hover:bg-white transition"
        >
          Go Home
        </Link>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && (value.length > 10 || /[^0-9]/.test(value))) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const waMessage = `Interest: ${formData.topic}\nName: ${formData.name}\nPhone: ${formData.phone}\nMessage: ${formData.message}`;
    window.open(
      `https://wa.me/919625975135?text=${encodeURIComponent(waMessage)}`,
      "_blank",
    );
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <div className="bg-[#0A0A0A] text-gray-300 min-h-screen selection:bg-[#B68D40] selection:text-black">
      <Helmet>
        <title>{service.helmetTitle}</title>
        <meta name="description" content={service.helmetDescription} />
        <meta name="keywords" content={service.helmetKeywords} />
        <noscript>{service.helmetnoscript}</noscript>
        <link rel="canonical" href={`https://skslawgroupsandassociates.com/services/${service.id}`} />
      </Helmet>

      <Header />

      {/* --- PREMIUM HERO SECTION --- */}
      <div className="relative h-[60vh] flex items-end pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={service.bannerImage}
            className="w-full h-full object-cover opacity-40 scale-105"
            alt="Banner"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <nav className="flex items-center gap-2 text-[#B68D40] text-sm font-medium mb-6 uppercase tracking-widest">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight size={14} />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight size={14} />
            <span className="text-gray-500">{service.title}</span>
          </nav>
          <h1 className="text-5xl md:text-8xl font-black text-white leading-none tracking-tighter">
            {service.title.split(" ").map((word, i) => (
              <span key={i} className={i % 2 !== 0 ? "text-[#B68D40]" : ""}>
                {word}{" "}
              </span>
            ))}
          </h1>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* --- CONTENT AREA (LEFT) --- */}
          <div className="lg:col-span-8 space-y-24">
            {/* INTRO Section */}
            <section className="relative">
              <div className="absolute -left-6 top-0 w-1 h-full bg-[#B68D40]" />
              <h2 className="text-3xl font-bold text-white mb-8 tracking-tight">
                Executive Summary
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed font-light">
                {service.fullDesc?.intro}
              </p>
            </section>

            {/* SECTIONS */}
            {Array.isArray(service.fullDesc?.sections) &&
              service.fullDesc.sections.map((section, i) => (
                <section key={i} className="space-y-8">
                  <div className="flex items-center gap-4">
                    <span className="text-[#B68D40] font-mono text-lg">
                      0{i + 1}/
                    </span>
                    <h3 className="text-2xl font-bold text-white uppercase tracking-wider">
                      {section.title}
                    </h3>
                  </div>

                  {section.content && (
                    <p className="text-gray-400 leading-relaxed">
                      {section.content}
                    </p>
                  )}

                  {section.list && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.list.map((item, idx) => (
                        <div
                          key={idx}
                          className="group flex items-start gap-4 p-5 bg-[#121212] border border-white/5 rounded-2xl hover:border-[#B68D40]/50 transition-all duration-300"
                        >
                          <CheckCircle2
                            className="text-[#B68D40] mt-1 shrink-0"
                            size={20}
                          />
                          <span className="group-hover:text-white transition">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ))}

            {/* PORTFOLIO HIGHLIGHTS */}
            <section className="space-y-10">
              <h3 className="text-3xl font-bold text-white">
                Portfolio Highlights
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {Array.isArray(service.featureDetails) &&
                  service.featureDetails.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => setSelectedItem(item)}
                      className="group relative overflow-hidden rounded-3xl bg-[#121212] cursor-pointer"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={item.img}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          alt={item.name}
                        />
                      </div>
                      <div className="p-6">
                        <h4 className="text-lg font-bold text-white group-hover:text-[#B68D40] transition">
                          {item.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {item.desc}
                        </p>
                      </div>
                      <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md p-2 rounded-full border border-white/10 opacity-0 group-hover:opacity-100 transition">
                        <Globe size={16} className="text-[#B68D40]" />
                      </div>
                    </div>
                  ))}
              </div>
            </section>
          </div>

          {/* --- SIDEBAR (RIGHT) --- */}
          <div className="lg:col-span-4">
            <div className="sticky top-32 space-y-8">
              {/* CONTACT FORM CARD */}
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-b from-[#B68D40] to-transparent rounded-3xl opacity-20 blur group-hover:opacity-40 transition" />
                <div className="relative bg-[#121212] border border-white/10 p-8 rounded-3xl shadow-2xl">
                  <h3 className="text-xl font-bold text-white mb-1">
                    Inquiry Form
                  </h3>
                  <p className="text-sm text-gray-500 mb-8 font-mono">
                    RE: {service.title}
                  </p>

                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-widest text-[#B68D40] font-bold">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-black/50 border border-white/5 p-4 rounded-xl text-white focus:border-[#B68D40] outline-none transition"
                        placeholder="Full Name...."
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-widest text-[#B68D40] font-bold">
                        Contact Details
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-black/50 border border-white/5 p-4 rounded-xl text-white focus:border-[#B68D40] outline-none mb-3"
                        placeholder="Phone Number"
                        required
                      />
                    </div>

                    <textarea
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-black/50 border border-white/5 p-4 rounded-xl text-white focus:border-[#B68D40] outline-none transition"
                      placeholder="Describe your requirements..."
                    />

                    <button
                      type="submit"
                      className="w-full bg-[#B68D40] text-black font-black py-4 rounded-xl flex items-center justify-center gap-3 hover:bg-white transition-all duration-500"
                    >
                      SEND INQUIRY <Send size={18} />
                    </button>
                    {success && (
                      <p className="text-center text-xs text-[#B68D40] animate-pulse">
                        Request sent successfully via WhatsApp!
                      </p>
                    )}
                  </form>
                </div>
              </div>

              {/* INFO BOX */}
              <div className="bg-[#121212]/50 border-l-2 border-[#B68D40] p-6 rounded-r-2xl space-y-4">
                <div className="flex items-center gap-4 group cursor-pointer">
                  <div className="p-3 bg-black rounded-full group-hover:bg-[#B68D40] transition">
                    <Phone
                      size={18}
                      className="text-[#B68D40] group-hover:text-black"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500 font-bold">
                      Call Us
                    </p>
                    <p className="text-white font-medium">+91 98181 04905</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group cursor-pointer">
                  <div className="p-3 bg-black rounded-full group-hover:bg-[#B68D40] transition">
                    <Mail
                      size={18}
                      className="text-[#B68D40] group-hover:text-black"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500 font-bold">
                      Email
                    </p>
                    <p className="text-white font-medium break-all">
                      Shailendrakumarsingh9818@gmail.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* --- LIGHTBOX POPUP --- */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[100] p-6">
          <div className="bg-[#121212] max-w-2xl w-full rounded-3xl overflow-hidden border border-white/10 relative">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-5 right-5 z-10 bg-white text-black w-10 h-10 rounded-full flex items-center justify-center font-bold hover:bg-[#B68D40] transition"
            >
              ✕
            </button>
            <img
              src={selectedItem.img}
              className="w-full h-80 object-cover"
              alt="Detail"
            />
            <div className="p-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                {selectedItem.name}
              </h2>
              <p className="text-gray-400 leading-relaxed">
                {selectedItem.desc}
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ServicesDetail;
